import React, { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useToast } from "../context/ToastContext";
import { useProjectMembers } from "../context/ProjectMembersContext";
import { COMMENT_TYPES } from "../constants/commentConstants";

function parseCommentsResponse(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.comments)) return data.comments;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
}

/**
 * API often returns UTC as "YYYY-MM-DD HH:mm:ss" with no offset; without "Z",
 * JS treats that as local time and labels appear "wrong". Normalize to UTC then
 * format in the viewer's locale (actual local timezone).
 */
function parseCommentInstant(iso) {
  if (!iso) return null;
  const s = String(iso).trim();
  if (!s) return null;
  // Already has explicit offset or Z
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // ISO date-time with T, no zone → treat as UTC
  if (/^\d{4}-\d{2}-\d{2}T\d/.test(s)) {
    const d = new Date(`${s.replace(/\.\d+$/, "")}Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DD HH:mm:ss.ffffff" → UTC
  if (/^\d{4}-\d{2}-\d{2} \d/.test(s)) {
    const noFrac = s.replace(/\.\d+$/, "");
    const d = new Date(`${noFrac.replace(" ", "T")}Z`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatMessageLocalTime(iso) {
  const d = parseCommentInstant(iso);
  if (!d) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${mm}/${dd}/${yyyy}, ${time}`;
}

function sortCommentsAsc(list) {
  return [...list].sort(
    (a, b) =>
      (parseCommentInstant(a.created_at)?.getTime() ?? 0) -
      (parseCommentInstant(b.created_at)?.getTime() ?? 0),
  );
}

/** Matches HouseholdCommentResponse: `{ user: { id, name } }`. */
function commentAuthorUserId(c) {
  const id = c.user?.id;
  if (id == null || id === "") return null;
  return id;
}

function displayName(c) {
  const name = typeof c.user?.name === "string" ? c.user.name.trim() : "";
  return name || "Team member";
}

function isNigoComment(c) {
  return c.comment_type === COMMENT_TYPES.NIGO;
}

function isSystemComment(c) {
  return c.comment_type === COMMENT_TYPES.SYSTEM;
}

function memberFullName(m) {
  return `${m?.first_name || ""} ${m?.last_name || ""}`.trim();
}

function filterProjectMembers(members, query) {
  const q = query.trim().toLowerCase();
  const base = q
    ? members.filter((m) => {
        const first = String(m?.first_name || "").toLowerCase();
        const last = String(m?.last_name || "").toLowerCase();
        const full = memberFullName(m).toLowerCase();
        return first.includes(q) || last.includes(q) || full.includes(q);
      })
    : members;
  return base.slice(0, 6);
}

function computeMention(value, caret) {
  const before = value.slice(0, caret);
  const match = before.match(/(^|\s)@(\w*)$/);
  if (!match) return { active: false, query: "", start: 0, caret };
  const query = match[2];
  return { active: true, query, start: caret - query.length - 1, caret };
}

function escapeRegExp(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function renderMentions(text, mine, keyPrefix) {
  const parts = String(text).split(/(@\w+)/);
  return parts.map((part, i) => {
    if (/^@\w+$/.test(part)) {
      return (
        <span
          key={`${keyPrefix}-${i}`}
          className={
            mine
              ? "rounded bg-white/25 px-1 py-0.5 font-medium text-white"
              : "rounded bg-emerald-100 px-1 py-0.5 font-medium text-emerald-800"
          }
        >
          {part}
        </span>
      );
    }
    return <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>;
  });
}

function isParentheticalLine(line) {
  return /^\(.+\)$/.test(line.trim());
}

function renderMessageWithMentions(message, mine, italicizeParentheticals = false) {
  if (!message) return message;
  if (!italicizeParentheticals) return renderMentions(message, mine, "msg");

  const lines = String(message).split("\n");
  return lines.map((line, i) => (
    <React.Fragment key={`line-${i}`}>
      {i > 0 && "\n"}
      {isParentheticalLine(line) ? (
        <em className={mine ? "text-emerald-50/90" : "text-gray-500"}>{line}</em>
      ) : (
        renderMentions(line, mine, `line-${i}`)
      )}
    </React.Fragment>
  ));
}

function HouseholdCommentsModal({
  isOpen,
  onClose,
  householdId,
  projectId,
  currentUserId,
  userRole,
  onMarkPendingForExecution,
  onMarkDocusignInitiated,
  onMarkCompleted,
  status,
  subStatus,
}) {
  const { toast } = useToast();
  const { membersByProject, fetchProjectMembers } = useProjectMembers();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [draft, setDraft] = useState("");
  const [mention, setMention] = useState({ active: false, query: "", start: 0, caret: 0 });
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [recordedMentions, setRecordedMentions] = useState([]);
  const listEndRef = useRef(null);
  const inputRef = useRef(null);
  const pendingCaretRef = useRef(null);

  const isAdvisor = userRole && String(userRole).toLowerCase() === "advisor";
  const isOps = userRole && String(userRole).toLowerCase() === "ops";
  const isAdmin = userRole && String(userRole).toLowerCase() === "admin";

  const handleMarkPendingForExecution = async () => {
    if (!householdId || transferring || !onMarkPendingForExecution) return;
    setTransferring(true);
    try {
      await onMarkPendingForExecution();
      onClose();
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Failed to mark as pending for execution.";
      toast.error(typeof msg === "string" ? msg : "Failed to mark as pending for execution.");
    } finally {
      setTransferring(false);
    }
  };

  const handleMarkDocusignInitiated = async () => {
    if (!householdId || transferring || !onMarkDocusignInitiated) return;
    setTransferring(true);
    try {
      await onMarkDocusignInitiated();
      onClose();
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Failed to mark as docusign initiated.";
      toast.error(typeof msg === "string" ? msg : "Failed to mark as docusign initiated.");
    } finally {
      setTransferring(false);
    }
  };

  const handleMarkCompleted = async () => {
    if (!householdId || transferring || !onMarkCompleted) return;
    setTransferring(true);
    try {
      await onMarkCompleted();
      onClose();
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Failed to mark as completed.";
      toast.error(typeof msg === "string" ? msg : "Failed to mark as completed.");
    } finally {
      setTransferring(false);
    }
  };

  const scrollToBottom = useCallback(() => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const fetchComments = useCallback(async () => {
    if (!householdId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await axiosInstance.get(
        `/households/${householdId}/comments`,
        { params: { limit: 100, offset: 0 } },
      );
      setComments(sortCommentsAsc(parseCommentsResponse(data)));
    } catch (e) {
      const msg =
        e.response?.data?.detail ||
        e.response?.data?.message ||
        "Could not load comments.";
      setLoadError(typeof msg === "string" ? msg : "Could not load comments.");
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  useEffect(() => {
    if (!isOpen || !householdId) return;
    fetchComments();
    setRecordedMentions([]);
    setMention({ active: false, query: "", start: 0, caret: 0 });
    setHighlightIndex(0);
    axiosInstance
      .post(`/households/${householdId}/comments/mark-read`)
      .catch(() => {});
    const t = setTimeout(() => inputRef.current?.focus(), 200);
    return () => clearTimeout(t);
  }, [isOpen, householdId, fetchComments]);

  useEffect(() => {
    if (!isOpen || !projectId) return;
    fetchProjectMembers(projectId);
  }, [isOpen, projectId, fetchProjectMembers]);

  useEffect(() => {
    if (pendingCaretRef.current == null || !inputRef.current) return;
    const pos = pendingCaretRef.current;
    pendingCaretRef.current = null;
    inputRef.current.focus();
    inputRef.current.setSelectionRange(pos, pos);
  }, [draft]);

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [isOpen, comments, loading, scrollToBottom]);

  const closeMention = () => {
    setMention({ active: false, query: "", start: 0, caret: 0 });
    setHighlightIndex(0);
  };

  const computeMentionedUserIds = (text) => {
    const ids = new Set();
    recordedMentions.forEach((m) => {
      const re = new RegExp(`(^|[^\\w@])${escapeRegExp(m.token)}(?![\\w])`);
      if (re.test(text)) ids.add(m.user_id);
    });
    return [...ids];
  };

  const handleDraftChange = (e) => {
    const value = e.target.value;
    const caret = e.target.selectionStart ?? value.length;
    setDraft(value);
    setMention(computeMention(value, caret));
    setHighlightIndex(0);
  };

  const syncMentionFromCaret = (e) => {
    if (["ArrowUp", "ArrowDown", "Enter", "Escape"].includes(e.key)) return;
    const el = e.target;
    setMention(computeMention(el.value, el.selectionStart ?? el.value.length));
    setHighlightIndex(0);
  };

  const selectMember = (member) => {
    if (!member) return;
    const caret = mention.caret ?? draft.length;
    const start = mention.start;
    const token = `@${String(member.first_name || "").trim()}`;
    const before = draft.slice(0, start);
    const after = draft.slice(caret);
    const insertion = `${token} `;
    const newText = `${before}${insertion}${after}`;
    pendingCaretRef.current = before.length + insertion.length;
    setDraft(newText);
    setRecordedMentions((prev) => [...prev, { user_id: member.user_id, token }]);
    closeMention();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || !householdId || sending) return;

    setSending(true);
    try {
      const { data } = await axiosInstance.post(
        `/households/${householdId}/comments`,
        { message: text, mentioned_user_ids: computeMentionedUserIds(text) },
      );
      setDraft("");
      setRecordedMentions([]);
      closeMention();
      if (data && data.id && data.user?.id != null) {
        setComments((prev) => sortCommentsAsc([...prev, data]));
      } else {
        await fetchComments();
      }
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        "Failed to send.";
      toast.error(typeof msg === "string" ? msg : "Failed to send.");
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  if (!isOpen) return null;

  const projectMembers = membersByProject[projectId] || [];
  const projectMemberCandidates = projectMembers.filter(
    (m) => String(m?.user_id) !== String(currentUserId),
  );
  const mentionResults = mention.active
    ? filterProjectMembers(projectMemberCandidates, mention.query)
    : [];
  const showMentionDropdown = mention.active && mentionResults.length > 0;
  const safeHighlight = Math.min(
    highlightIndex,
    Math.max(0, mentionResults.length - 1),
  );

  const onKeyDown = (e) => {
    if (showMentionDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((i) => (i + 1) % mentionResults.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex(
          (i) => (i - 1 + mentionResults.length) % mentionResults.length,
        );
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        selectMember(mentionResults[safeHighlight]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeMention();
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isMine = (c) => {
    const authorId = commentAuthorUserId(c);
    return (
      currentUserId != null &&
      authorId != null &&
      String(authorId) === String(currentUserId)
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="household-comments-title"
      onClick={onClose}
    >
      <div
        className="flex h-[min(92vh,1280px)] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-emerald-100/80 bg-white shadow-2xl shadow-emerald-900/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-emerald-100 bg-gradient-to-r from-emerald-50/90 to-teal-50/50 px-5 py-4">
          <div>
            <h2
              id="household-comments-title"
              className="text-lg font-bold text-emerald-950"
            >
              Household Comments
            </h2>
            <p className="mt-0.5 text-xs font-medium text-emerald-800/70">
              Private thread for your team on this household
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-emerald-800/60 transition-colors hover:bg-white/80 hover:text-emerald-900"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto bg-gradient-to-b from-slate-50/80 to-emerald-50/20 px-5 py-5 sm:px-6">
          {status !== "docusign_initiated" && status !== "data_collection_completed" && !(subStatus === "pending_for_execution") && (
            <div className="mb-6 border-b border-amber-100 bg-amber-50/60 px-5 py-3 flex items-center justify-between gap-3 rounded-xl shadow-sm">
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-amber-900">Marks this household as data collection completed</span>
              </div>
              <button
                type="button"
                onClick={handleMarkPendingForExecution}
                disabled={transferring}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold shadow transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {transferring ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Marking…
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                    Data Collection Completed
                  </>
                )}
              </button>
            </div>
          )}


          {isAdmin && status === "docusign_initiated" && (
            <div className="mb-6 border-b border-emerald-100 bg-emerald-50/60 px-5 py-3 flex items-center justify-between gap-3 rounded-xl shadow-sm">
              <div className="flex flex-col">
                <span className="text-xs font-semibold text-emerald-900">Mark this Household as completed</span>
              </div>
              <button
                type="button"
                onClick={handleMarkCompleted}
                disabled={transferring}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold shadow transition-all hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {transferring ? (
                  <>
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Updating…
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Mark as Completed
                  </>
                )}
              </button>
            </div>
          )}
          {loading && (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-emerald-800/70">
              <svg
                className="h-8 w-8 animate-spin text-emerald-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm">Loading conversation…</span>
            </div>
          )}

          {!loading && loadError && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-900">
              {loadError}
            </div>
          )}

          {!loading && !loadError && comments.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <div className="rounded-full bg-emerald-100/80 p-4 text-emerald-700">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <p className="max-w-xs text-sm font-medium text-emerald-900/80">
                No messages yet.
              </p>
            </div>
          )}

          {!loading &&
            !loadError &&
            comments.map((c) => {
              const mine = isMine(c);
              const nigo = isNigoComment(c);
              return (
                <div
                  key={c.id}
                  className={`mb-4 flex w-full flex-col ${mine ? "items-end" : "items-start"}`}
                >
                  {!mine && (
                    <div
                      className={`mb-1 max-w-[min(100%,24rem)] truncate px-0.5 text-[11px] font-semibold uppercase tracking-wide ${
                        nigo ? "text-red-600" : "text-emerald-900/55"
                      }`}
                    >
                      {nigo ? "NIGO" : displayName(c)}
                    </div>
                  )}
                  <div
                    className={`min-w-0 rounded-2xl px-3.5 py-2.5 sm:px-4 ${mine
                      ? "w-fit max-w-[min(92%,36rem)] rounded-br-md bg-emerald-600 text-white shadow-md shadow-emerald-900/15"
                      : nigo
                        ? "w-fit max-w-[min(92%,36rem)] rounded-2xl rounded-bl-md border border-red-200/70 bg-gradient-to-br from-red-50/95 to-rose-50/80 text-gray-900 shadow-sm ring-1 ring-red-100/80"
                        : "w-fit max-w-[min(92%,36rem)] rounded-2xl rounded-bl-md border border-emerald-200/50 bg-gradient-to-br from-emerald-50/98 to-teal-50/80 text-gray-900 shadow-sm ring-1 ring-emerald-100/80"
                      }`}
                  >
                    <p
                      className={`whitespace-pre-wrap break-words text-[15px] leading-relaxed ${mine ? "text-white" : "text-gray-800"
                        }`}
                    >
                      {renderMessageWithMentions(c.message, mine, isSystemComment(c))}
                    </p>
                    <div
                      className={`mt-1.5 text-[11px] leading-tight tabular-nums ${mine
                        ? "text-right text-emerald-100/95"
                        : nigo
                          ? "text-left text-red-800/50"
                          : "text-left text-emerald-800/50"
                        }`}
                    >
                      {formatMessageLocalTime(c.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
          <div ref={listEndRef} />
        </div>

        <form
          onSubmit={handleSubmit}
          className="border-t border-emerald-100 bg-white/95 p-3 backdrop-blur-sm"
        >
          <div className="flex items-end gap-2">
            <div className="relative flex-1">
              {showMentionDropdown && (
                <ul
                  role="listbox"
                  className="absolute bottom-full left-0 z-10 mb-2 max-h-60 w-full overflow-y-auto rounded-xl border border-emerald-200 bg-white py-1 shadow-lg shadow-emerald-900/10"
                >
                  {mentionResults.map((m, idx) => (
                    <li
                      key={m.user_id}
                      role="option"
                      aria-selected={idx === safeHighlight}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectMember(m);
                      }}
                      onMouseEnter={() => setHighlightIndex(idx)}
                      className={`cursor-pointer px-3 py-2 text-sm ${
                        idx === safeHighlight
                          ? "bg-emerald-50 text-emerald-900"
                          : "text-gray-700"
                      }`}
                    >
                      <div className="font-medium">{memberFullName(m)}</div>
                      {m.email && (
                        <div className="truncate text-[11px] text-gray-400">
                          {m.email}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <input
                ref={inputRef}
                type="text"
                value={draft}
                onChange={handleDraftChange}
                onKeyDown={onKeyDown}
                onKeyUp={syncMentionFromCaret}
                onClick={syncMentionFromCaret}
                placeholder="Write a message…"
                disabled={sending || loading}
                maxLength={4000}
                className="min-h-[44px] w-full rounded-full border border-emerald-200/80 bg-emerald-50/30 px-4 text-sm text-gray-900 shadow-inner shadow-emerald-900/5 outline-none transition-all placeholder:text-gray-400 focus:border-emerald-500 focus:bg-white focus:ring-2 focus:ring-emerald-500/25 disabled:opacity-60"
              />
            </div>
            <button
              type="submit"
              disabled={sending || loading || !draft.trim()}
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-md shadow-emerald-900/15 transition-all hover:bg-emerald-700 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
              aria-label="Send message"
            >
              {sending ? (
                <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              ) : (
                <svg className="h-5 w-5 -translate-x-px translate-y-px" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              )}
            </button>
          </div>
          <p className="mt-2 px-1 text-center text-[11px] text-gray-400">
            Enter to send
          </p>
        </form>
      </div>
    </div>
  );
}

export default HouseholdCommentsModal;
